import {
  ActionIcon,
  Badge,
  Button,
  Divider,
  Grid,
  Group,
  List,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { IconFileCheck, IconFileX, IconInfoCircle, IconMinus, IconPlus } from '@tabler/icons-react';
import { useEffect, useMemo } from 'react';
import {
  CONTACT_KIND_LABELS,
  CONTACT_KINDS,
  CONTACT_RELATIONSHIP_TYPES,
  ROI_PERMISSION_LABELS,
  type Contact,
  type ContactKind,
  type ContactRelationshipType,
  type RoiPermissionKey,
} from '../data/contactTypes';
import {
  clientContactFormSchema,
  clientContactPhoneTypeValues,
  clientContactFormValuesToContactPayload,
  contactToFormValues,
  getDefaultClientContactFormValues,
  type ClientContactFormValues,
} from '@/schemas/client-contact.schema';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming',
];

function formatRoiDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function RoiBannerTooltipContent({ contact }: { contact: Contact }): JSX.Element {
  const roi = contact.roi;
  if (!roi) return <></>;

  const granted = (Object.keys(ROI_PERMISSION_LABELS) as RoiPermissionKey[]).filter(
    (k) => roi.permissions[k],
  );

  return (
    <Stack gap={6} maw={280}>
      <Text size="xs" fw={700}>Authorized disclosures:</Text>
      {granted.length === 0 ? (
        <Text size="xs" c="dimmed">No specific disclosures recorded.</Text>
      ) : (
        <List size="xs" spacing={2}>
          {granted.map((k) => (
            <List.Item key={k}>
              {ROI_PERMISSION_LABELS[k]}
              {k === 'other' && roi.otherDescription ? ` — ${roi.otherDescription}` : ''}
            </List.Item>
          ))}
        </List>
      )}
      {roi.expirationDate && (
        <Text size="xs" c="yellow.3" mt={4}>
          Expires {formatRoiDate(roi.expirationDate)}
        </Text>
      )}
    </Stack>
  );
}

function RoiBanner({ contact }: { contact: Contact }): JSX.Element {
  const roi = contact.roi;

  if (!roi) {
    return (
      <Paper withBorder p="sm" radius="md" style={{ borderColor: 'var(--mantine-color-gray-3)', backgroundColor: 'var(--mantine-color-gray-0)' }}>
        <Group gap="xs" wrap="nowrap">
          <IconFileX size={18} color="var(--mantine-color-gray-5)" style={{ flexShrink: 0 }} />
          <div>
            <Text size="sm" fw={600} c="gray.6">No ROI on file</Text>
            <Text size="xs" c="gray.5">Client has not signed a Release of Information for this contact.</Text>
          </div>
        </Group>
      </Paper>
    );
  }

  const grantedCount = (Object.keys(roi.permissions) as RoiPermissionKey[]).filter(
    (k) => roi.permissions[k],
  ).length;

  const isExpired = roi.expirationDate ? new Date(roi.expirationDate) < new Date() : false;

  return (
    <Paper
      withBorder
      p="sm"
      radius="md"
      style={{
        borderColor: isExpired ? 'var(--mantine-color-orange-4)' : 'var(--mantine-color-teal-4)',
        backgroundColor: isExpired ? 'var(--mantine-color-orange-0)' : 'var(--mantine-color-teal-0)',
      }}
    >
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <IconFileCheck
            size={18}
            color={isExpired ? 'var(--mantine-color-orange-6)' : 'var(--mantine-color-teal-6)'}
            style={{ flexShrink: 0 }}
          />
          <div style={{ minWidth: 0 }}>
            <Group gap={6} align="center">
              <Text size="sm" fw={600} c={isExpired ? 'orange.7' : 'teal.7'}>
                ROI on file
              </Text>
              <Badge
                size="xs"
                variant="light"
                color={isExpired ? 'orange' : 'teal'}
              >
                {isExpired ? 'Expired' : 'Active'}
              </Badge>
            </Group>
            <Text size="xs" c="gray.6">
              Signed {formatRoiDate(roi.dateSigned)}
              {roi.expirationDate ? ` · Expires ${formatRoiDate(roi.expirationDate)}` : ' · No expiration'}
              {' · '}{grantedCount} disclosure{grantedCount !== 1 ? 's' : ''} authorized
            </Text>
          </div>
        </Group>
        <Tooltip
          label={<RoiBannerTooltipContent contact={contact} />}
          multiline
          withArrow
          position="bottom-end"
          color="dark"
          styles={{ tooltip: { padding: '10px 12px' } }}
        >
          <ActionIcon variant="subtle" color={isExpired ? 'orange' : 'teal'} radius="xl" size="sm" aria-label="View ROI permissions">
            <IconInfoCircle size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Paper>
  );
}

export interface ClientContactFormModalProps {
  opened: boolean;
  onClose: () => void;
  /** `view` = read-only (Medical Record); `create` / `edit` = Contacts tab */
  mode: 'create' | 'edit' | 'view';
  contact: Contact | null;
  onSave?: (payload: Omit<Contact, 'id'> & { id?: string }) => void | Promise<void>;
}

export function ClientContactFormModal({
  opened,
  onClose,
  mode,
  contact,
  onSave,
}: ClientContactFormModalProps): JSX.Element {
  const isView = mode === 'view';
  const isEdit = mode === 'edit';

  /** Stable reference so Mantine always invokes the same resolver function. */
  const clientContactResolver = useMemo(() => zod4Resolver(clientContactFormSchema), []);

  const form = useForm<ClientContactFormValues>({
    initialValues: getDefaultClientContactFormValues(),
    validate: isView ? undefined : clientContactResolver,
  });

  useEffect(() => {
    if (!opened) return;
    if (isView && contact) {
      form.setValues(contactToFormValues(contact));
      return;
    }
    if (mode === 'create' || !contact) {
      form.setValues(getDefaultClientContactFormValues());
      return;
    }
    form.setValues(contactToFormValues(contact));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when modal opens / mode / contact identity changes
  }, [opened, mode, contact?.id, isView]);

  const modalTitle =
    mode === 'view' ? 'Contact details' : isEdit ? 'Edit Contact' : 'Add Contact';

  const handleSubmit = form.onSubmit(
    async (values) => {
      if (isView || !onSave) return;
      const parsed = clientContactFormSchema.safeParse(values);
      if (!parsed.success) {
        const errs: Record<string, string> = {};
        for (const issue of parsed.error.issues) {
          errs[issue.path.join('.')] = issue.message;
        }
        form.setErrors(errs);
        notifications.show({
          color: 'red',
          title: 'Please fix the form',
          message: 'Some fields are invalid. Review the errors below.',
        });
        return;
      }
      const raw = clientContactFormValuesToContactPayload(parsed.data as ClientContactFormValues);
      const payload: Omit<Contact, 'id'> & { id?: string } = { ...raw };
      if (isEdit && contact) {
        payload.id = contact.id;
        payload.source = contact.source ?? payload.source;
        payload.relatedPersonId = contact.relatedPersonId;
        payload.contactKind = contact.contactKind ?? payload.contactKind;
      }
      await onSave(payload);
    },
    () => {
      notifications.show({
        color: 'red',
        title: 'Please fix the form',
        message: 'Some fields are invalid. Review the errors below.',
      });
    },
  );

  const relationshipType = form.values.relationshipType as ContactRelationshipType | '';
  const contactKind = form.values.contactKind as ContactKind;

  const setContactKind = (kind: ContactKind) => {
    form.setFieldValue('contactKind', kind);
    form.setFieldValue('source', kind === 'emergency-contact' ? 'patient-contact' : 'related-person');
    form.setFieldValue('isEmergencyContact', kind === 'emergency-contact');
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={modalTitle}
      size="lg"
      centered
      styles={{
        title: { fontWeight: 600, fontSize: '1.125rem' },
        header: { borderBottom: '1px solid var(--mantine-color-gray-3)', paddingBottom: 'var(--mantine-spacing-md)' },
        body: { paddingTop: 0, paddingBottom: 0 },
      }}
    >
      <form onSubmit={handleSubmit}>
        <ScrollArea.Autosize mah="min(70vh, 36rem)" type="auto" offsetScrollbars>
        <Stack gap="md" py="md">
          {(isView || isEdit) && contact && <RoiBanner contact={contact} />}

          <div>
            <Title order={6} mb="sm" c="dark">Contact type</Title>
            <Select
              placeholder="— select —"
              value={contactKind}
              onChange={(v) => {
                if (v) setContactKind(v as ContactKind);
              }}
              data={CONTACT_KINDS.map((k) => ({ value: k, label: CONTACT_KIND_LABELS[k] }))}
              withAsterisk={!isView}
              readOnly={isView || isEdit}
              disabled={isView || isEdit}
              description={
                isEdit
                  ? 'Contact type is fixed after create so the FHIR source stays consistent.'
                  : 'Emergency contacts are stored on the chart. Related persons and guarantors are RelatedPerson records.'
              }
            />
          </div>

          <Divider />

          <div>
            <Title order={6} mb="sm" c="dark">Name</Title>
            <Grid gutter="sm">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Legal first name"
                  placeholder="First name"
                  {...form.getInputProps('legalFirstName')}
                  withAsterisk={!isView}
                  readOnly={isView}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Middle name"
                  placeholder="Middle"
                  {...form.getInputProps('middleName')}
                  readOnly={isView}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Legal last name"
                  placeholder="Last name"
                  {...form.getInputProps('legalLastName')}
                  withAsterisk={!isView}
                  readOnly={isView}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Suffix"
                  placeholder="Jr., Sr., III"
                  {...form.getInputProps('suffix')}
                  readOnly={isView}
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label="What name do they go by?"
                  placeholder="Preferred or nickname"
                  {...form.getInputProps('preferredName')}
                  description={isView ? undefined : 'Used in place of first name in client communication (reminders, billing documents, etc.).'}
                  readOnly={isView}
                />
              </Grid.Col>
            </Grid>
          </div>

          <Divider />

          <div>
            <Title order={6} mb="sm" c="dark">Relationship</Title>
            <Select
                label="Relationship type"
                placeholder="— select —"
                value={relationshipType || null}
                onChange={(v) => form.setFieldValue('relationshipType', (v ?? '') as ClientContactFormValues['relationshipType'])}
                data={CONTACT_RELATIONSHIP_TYPES.map((t) => ({ value: t, label: t }))}
                withAsterisk={!isView}
                style={{ flex: 1, minWidth: 180 }}
                readOnly={isView}
                disabled={isView}
                error={form.errors.relationshipType}
              />
            {(relationshipType === 'Other' || relationshipType === 'Physician') && (
              <Grid gutter="sm" mt="sm">
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="Role"
                    placeholder={relationshipType === 'Physician' ? 'e.g. Primary Care Physician' : 'e.g. Case Manager'}
                    {...form.getInputProps('role')}
                    readOnly={isView}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="Organization"
                    placeholder="Organization name"
                    {...form.getInputProps('organization')}
                    readOnly={isView}
                  />
                </Grid.Col>
              </Grid>
            )}
          </div>

          <Divider />

          <div>
            <Title order={6} mb="xs" c="dark">Contact details</Title>
            {!isView && (
              <Text size="xs" c="gray.6" mb="sm">
                Manage contact info for reminders and notifications. An email is needed for granting Client Portal access.
              </Text>
            )}
            <Stack gap="sm">
              <div>
                <Text size="sm" fw={500} mb={4}>Email</Text>
                {form.values.emails.map((emailVal, idx) => (
                  <Group key={idx} align="flex-end" gap="sm" mb="xs" wrap="nowrap">
                    <TextInput
                      placeholder="email@example.com"
                      value={emailVal}
                      onChange={(e) => {
                        const next = [...form.values.emails];
                        next[idx] = e.currentTarget.value;
                        form.setFieldValue('emails', next);
                      }}
                      style={{ flex: 1 }}
                      readOnly={isView}
                      withAsterisk={idx === 0 && !isView && contactKind === 'related-person'}
                      required={idx === 0 && !isView && contactKind === 'related-person'}
                      error={form.errors[`emails.${idx}`]}
                    />
                    {!isView && (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        onClick={() => form.setFieldValue(
                          'emails',
                          form.values.emails.filter((_, i) => i !== idx),
                        )}
                        disabled={form.values.emails.length <= 1}
                        aria-label="Remove email"
                      >
                        <IconMinus size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                ))}
                {!isView && (
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => form.setFieldValue('emails', [...form.values.emails, ''])}
                    color="gold"
                    type="button"
                  >
                    Add email
                  </Button>
                )}
              </div>
              <div>
                <Text size="sm" fw={500} mb={4}>
                  Phone number {!isView ? <Text span c="red">*</Text> : null}
                </Text>
                {!isView && (
                  <Text size="xs" c="gray.6" mb="xs">
                    10–15 digits; spaces and punctuation are ignored for validation.
                  </Text>
                )}
                {form.values.phones.map((phoneEntry, idx) => (
                  <Group key={idx} align="flex-end" gap="sm" mb="xs" wrap="nowrap">
                    <TextInput
                      placeholder="(555) 000-0000"
                      value={phoneEntry.value}
                      onChange={(e) => {
                        const next = [...form.values.phones];
                        next[idx] = { ...next[idx], value: e.currentTarget.value };
                        form.setFieldValue('phones', next);
                      }}
                      style={{ flex: 1 }}
                      readOnly={isView}
                      withAsterisk={idx === 0 && !isView}
                      required={idx === 0 && !isView}
                      error={form.errors[`phones.${idx}.value`]}
                    />
                    <Select
                      data={clientContactPhoneTypeValues.map((t) => ({ value: t, label: t }))}
                      value={phoneEntry.type}
                      onChange={(v) => {
                        const next = [...form.values.phones];
                        const t = (v ?? 'Mobile') as ClientContactFormValues['phones'][number]['type'];
                        next[idx] = { ...next[idx], type: t };
                        form.setFieldValue('phones', next);
                      }}
                      allowDeselect={false}
                      w={110}
                      readOnly={isView}
                      disabled={isView}
                    />
                    {!isView && (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        onClick={() => form.setFieldValue(
                          'phones',
                          form.values.phones.filter((_, i) => i !== idx),
                        )}
                        disabled={form.values.phones.length <= 1}
                        aria-label="Remove phone"
                        type="button"
                      >
                        <IconMinus size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                ))}
                {!isView && (
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => form.setFieldValue('phones', [...form.values.phones, { value: '', type: 'Mobile' }])}
                    color="gold"
                    type="button"
                  >
                    Add phone
                  </Button>
                )}
              </div>
            </Stack>
          </div>

          <Divider />

          <div>
            <Title order={6} mb="sm" c="dark">Address</Title>
            <Stack gap="sm">
              <TextInput
                label="Street"
                placeholder="Search address"
                {...form.getInputProps('street')}
                readOnly={isView}
              />
              <Grid gutter="sm">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <TextInput
                    label="City"
                    placeholder="City"
                    {...form.getInputProps('city')}
                    readOnly={isView}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Select
                    label="State"
                    placeholder="Select"
                    data={US_STATES.map((s) => ({ value: s, label: s }))}
                    {...form.getInputProps('state')}
                    clearable
                    readOnly={isView}
                    disabled={isView}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <TextInput
                    label="ZIP"
                    placeholder="12345 or 12345-6789"
                    {...form.getInputProps('zip')}
                    readOnly={isView}
                  />
                </Grid.Col>
              </Grid>
            </Stack>
          </div>

          <Divider />

          <div>
            <Textarea
              label="Notes"
              placeholder="Additional notes"
              {...form.getInputProps('notes')}
              minRows={3}
              autosize
              readOnly={isView}
            />
          </div>
        </Stack>
        </ScrollArea.Autosize>

          <Group
            justify="flex-end"
            gap="sm"
            py="md"
            style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}
          >
            {isView ? (
              <Button variant="default" onClick={onClose} type="button">
                Close
              </Button>
            ) : (
              <>
                <Button variant="default" onClick={onClose} type="button">
                  Cancel
                </Button>
                <Button color="gold" type="submit">
                  {isEdit ? 'Save Changes' : 'Save'}
                </Button>
              </>
            )}
          </Group>
      </form>
    </Modal>
  );
}
